/**
 * Sleep Scoring & Adaptive Algorithm
 *
 * Calculates personalized wake windows based on:
 * 1. Reference values (age-based, from scientific data)
 * 2. Observed data (actual wake windows from tracking)
 * 3. Parent feedback (ease rating, timing feel)
 *
 * The weighting shifts over time as more data is collected.
 */

import type { WakeWindowRange } from './wakeWindowCalculator';

// ─── Types ───────────────────────────────────────────────

export type SleepPlanMode = 'observe' | 'learn' | 'active';

export type DaySegment = 'morning' | 'midday' | 'afternoon' | 'evening';

export type FeedbackEase = 'easy' | 'normal' | 'hard';
export type FeedbackTiming = 'too_early' | 'just_right' | 'too_late';
export type FallAsleepDuration = 'under_5' | '5_to_15' | '15_to_30' | 'over_30';
export type SleepContext = 'bed' | 'arms' | 'stroller' | 'car' | 'carrier' | 'nursing';
export type ContextTag = 'teething' | 'growth_spurt' | 'sick' | 'bad_night' | 'vaccination' | 'regression' | 'new_place';
export type WakeUpMood = 'happy' | 'neutral' | 'crying';

export interface SleepFeedback {
  ease: FeedbackEase;
  timing?: FeedbackTiming;
  fallAsleepDuration?: FallAsleepDuration;
  sleepContext?: SleepContext[];
  contextTags?: ContextTag[];
  wakeUpMood?: WakeUpMood;
}

export interface SleepCycleScore {
  score: number;
  wakeWindowMinutes: number;
  daySegment: DaySegment;
  recencyWeight: number;
  contextWeight: number;
  napDurationMinutes: number;
}

export interface PersonalizedWakeWindow {
  optimalMinutes: number;
  rangeMin: number;
  rangeMax: number;
  confidence: number;
  isPersonalized: boolean;
  /** i18n-Key statt fertigem Text - der Wert wurde bisher hart auf Deutsch gesetzt. */
  sourceKey: string;
  /** Anzahl auswertbarer Zyklen, als Interpolationswert fuer sourceKey. */
  sourceCycleCount: number;
}

export interface ModeTransitionCheck {
  currentMode: SleepPlanMode;
  canUpgradeToLearn: boolean;
  canUpgradeToActive: boolean;
  /** Whether the user can manually select learn mode (>= 8 weeks) */
  canSelectLearn: boolean;
  /** Whether the user can manually select active mode (>= 12 weeks) */
  canSelectActive: boolean;
  recommendation?: string;
}

// ─── Konstanten ──────────────────────────────────────────

/**
 * Rollierendes Fenster fuer die Personalisierung in Tagen.
 * Nur Zyklen aus diesem Zeitraum praegen das persoenliche Wake Window -
 * aeltere Daten beschreiben einen Rhythmus, den das Baby laengst verlassen hat.
 */
export const ADAPTIVE_WINDOW_DAYS = 7;

/** Ab so vielen auswertbaren Zyklen wird personalisiert statt Referenz genutzt. */
export const MIN_CYCLES_FOR_PERSONALIZATION = 10;

// ─── Plausibilitaet ──────────────────────────────────────

/**
 * Altersabhaengige Grenzen fuer eine Wachphase, abgeleitet aus dem
 * Referenzfenster des Altersbands.
 *
 * Spiegelt sleep_plan_wake_window_bounds() in Migration 173 - beide Seiten
 * muessen dieselbe Regel anwenden, sonst schreibt der Client Werte, die die
 * Datenbank anschliessend wieder herausfiltert.
 *
 * Obergrenze = 2x Referenz-Maximum (120 Min beim Neugeborenen, 480 beim
 * Einjaehrigen). Eine pauschale Zahl taugt dafuer nicht: die bisher einzige
 * Pruefung im Code lag bei 480 Minuten und war damit fuer ein Neugeborenes
 * um den Faktor 8 zu lax.
 *
 * Untergrenze = 25% des Referenz-Minimums, mindestens 5 Minuten. Sie faengt
 * Doppelerfassungen ab, ohne echte kurze Wachphasen zu verwerfen.
 */
export function getPlausibleWakeWindowBounds(
  reference: WakeWindowRange,
): { min: number; max: number } {
  return {
    min: Math.max(5, Math.floor(reference.minMinutes * 0.25)),
    max: Math.ceil(reference.maxMinutes * 2),
  };
}

/**
 * Ist dieser Wert eine gemessene Wachphase oder eine Erfassungsluecke?
 *
 * Wird ein Schlaefchen nicht eingetragen, berechnet die App die Differenz zum
 * letzten vorhandenen Eintrag - das koennen Stunden oder Tage sein. Solche
 * Werte duerfen weder gespeichert noch ausgewertet werden.
 */
export function isPlausibleWakeWindow(
  minutes: number | null | undefined,
  reference: WakeWindowRange,
): boolean {
  if (minutes == null || !Number.isFinite(minutes)) return false;
  const { min, max } = getPlausibleWakeWindowBounds(reference);
  return minutes >= min && minutes <= max;
}

// ─── Day Segment ─────────────────────────────────────────

export function getDaySegment(hour: number): DaySegment {
  if (hour >= 5 && hour < 10) return 'morning';
  if (hour >= 10 && hour < 14) return 'midday';
  if (hour >= 14 && hour < 17) return 'afternoon';
  return 'evening';
}

// ─── Cycle Score Calculation ─────────────────────────────

export function calculateCycleScore(
  _wakeWindowMinutes: number,
  feedback: SleepFeedback,
  napDurationMinutes: number,
): number {
  let score = 0;

  // Ease rating (strongest indicator)
  switch (feedback.ease) {
    case 'easy': score += 0.5; break;
    case 'normal': score += 0.1; break;
    case 'hard': score -= 0.5; break;
  }

  // Timing feel (if provided)
  if (feedback.timing) {
    switch (feedback.timing) {
      case 'too_early': score -= 0.3; break;
      case 'just_right': score += 0.4; break;
      case 'too_late': score -= 0.4; break;
    }
  }

  // Fall asleep duration (if provided)
  if (feedback.fallAsleepDuration) {
    switch (feedback.fallAsleepDuration) {
      case 'under_5': score += 0.2; break;
      case '5_to_15': score += 0.1; break;
      case '15_to_30': score -= 0.1; break;
      case 'over_30': score -= 0.3; break;
    }
  }

  // Nap duration signal (full sleep cycle = positive)
  if (napDurationMinutes >= 45) score += 0.1;
  if (napDurationMinutes < 30) score -= 0.1;

  return Math.max(-1, Math.min(1, score));
}

// ─── Personalized Wake Window Calculation ────────────────

/**
 * Gewichteter Median.
 *
 * Ersetzt den frueheren gewichteten Mittelwert: ein einzelner Ausreisser
 * konnte das persoenliche Fenster verschieben, und dieses Fenster steuert
 * ueber sleep_plan_settings echte Push-Benachrichtigungen. Der Median haelt
 * die Gewichtung aus Score, Aktualitaet und Kontext bei, ohne dass ein
 * Extremwert das Ergebnis traegt.
 */
function weightedMedian(values: number[], weights: number[]): number {
  const pairs = values
    .map((value, i) => ({ value, weight: Math.max(0, weights[i] ?? 0) }))
    .filter(p => Number.isFinite(p.value) && p.weight > 0)
    .sort((a, b) => a.value - b.value);

  if (pairs.length === 0) return 0;

  const totalWeight = pairs.reduce((sum, p) => sum + p.weight, 0);
  let cumulative = 0;
  for (const pair of pairs) {
    cumulative += pair.weight;
    if (cumulative >= totalWeight / 2) return pair.value;
  }
  return pairs[pairs.length - 1].value;
}

export function calculatePersonalizedWakeWindow(
  recentCycles: SleepCycleScore[],
  referenceWindow: WakeWindowRange,
): PersonalizedWakeWindow {
  const refMin = referenceWindow.minMinutes;
  const refMax = referenceWindow.maxMinutes;
  const referenceMid = (refMin + refMax) / 2;
  const bounds = getPlausibleWakeWindowBounds(referenceWindow);

  // Erfassungsluecken aussortieren. Die RPC filtert bereits, aber der
  // Client verlaesst sich nicht darauf - beide Seiten wenden dieselbe Regel an.
  const usableCycles = recentCycles.filter(c =>
    isPlausibleWakeWindow(c.wakeWindowMinutes, referenceWindow),
  );

  // Nicht genug Daten → Referenzwerte
  if (usableCycles.length < MIN_CYCLES_FOR_PERSONALIZATION) {
    return {
      optimalMinutes: Math.round(referenceMid),
      rangeMin: refMin,
      rangeMax: refMax,
      confidence: Math.min(0.3, 0.05 + usableCycles.length * 0.025),
      isPersonalized: false,
      sourceKey: 'personalizedWindow.sourceReference',
      sourceCycleCount: usableCycles.length,
    };
  }

  // Zyklen mit positivem Score bilden den "sweet spot"
  const positiveCycles = usableCycles.filter(c => c.score > 0);

  const optimalFromData = positiveCycles.length > 0
    ? weightedMedian(
        positiveCycles.map(c => c.wakeWindowMinutes),
        positiveCycles.map(c => c.score * c.recencyWeight * c.contextWeight),
      )
    : null;

  // Gewichtung: mehr Daten → mehr Gewicht auf den eigenen Werten
  const dataWeight = Math.min(0.85, usableCycles.length / 50);
  const referenceWeight = 1 - dataWeight;

  const optimal = optimalFromData
    ? (optimalFromData * dataWeight) + (referenceMid * referenceWeight)
    : referenceMid;

  // Range: ±15% um das Optimum, aber innerhalb der altersabhaengigen Grenzen.
  // rangeMax war bisher nach oben offen (Math.max(refMax, optimal * 1.15)) -
  // genau dadurch konnte ein verschobenes Optimum ein Fenster von mehreren
  // Stunden erzeugen. Ein Baby darf laenger wach sein als die Referenz, aber
  // nicht laenger als das Doppelte des Referenz-Maximums.
  const clamp = (value: number) => Math.min(bounds.max, Math.max(bounds.min, value));
  const rangeMin = clamp(Math.max(refMin * 0.85, optimal * 0.85));
  const rangeMax = Math.max(rangeMin, clamp(Math.max(refMax, optimal * 1.15)));

  return {
    optimalMinutes: Math.round(clamp(optimal)),
    rangeMin: Math.round(rangeMin),
    rangeMax: Math.round(rangeMax),
    confidence: Math.min(0.95, 0.3 + (usableCycles.length * 0.013)),
    isPersonalized: true,
    sourceKey: 'personalizedWindow.sourceCycles',
    sourceCycleCount: usableCycles.length,
  };
}

// ─── Mode Transition Check ───────────────────────────────

export function checkModeTransition(
  currentMode: SleepPlanMode,
  correctedAgeWeeks: number,
  totalCyclesWithFeedback: number,
): ModeTransitionCheck {
  // Manual selection thresholds (lower)
  const canSelectLearn = correctedAgeWeeks >= 8;
  const canSelectActive = correctedAgeWeeks >= 12 && totalCyclesWithFeedback >= 10;

  // Auto-upgrade thresholds (higher)
  const canUpgradeToLearn = correctedAgeWeeks >= 10;
  const canUpgradeToActive = correctedAgeWeeks >= 14 && totalCyclesWithFeedback >= 10;

  // Key ohne Namespace-Praefix: die Aufrufer binden t() bereits an
  // 'sleepPlan'. Mit dem frueheren 'sleepPlan.'-Praefix loeste i18next
  // sleepPlan:sleepPlan.modeUpgrade.* auf, fand nichts und rendert den
  // rohen Key im Upgrade-Block.
  let recommendation: string | undefined;

  if (currentMode === 'observe' && canSelectLearn) {
    recommendation = 'modeUpgrade.learnAvailable';
  } else if (currentMode === 'learn' && canSelectActive) {
    recommendation = 'modeUpgrade.activeAvailable';
  }

  return {
    currentMode,
    canUpgradeToLearn,
    canUpgradeToActive,
    canSelectLearn,
    canSelectActive,
    recommendation,
  };
}

// ─── Confidence Label ────────────────────────────────────

// Keys ohne Namespace-Praefix, siehe checkModeTransition().
export function getConfidenceLevel(
  totalCycles: number,
): { level: 'very_low' | 'low' | 'medium' | 'good' | 'high'; labelKey: string } {
  if (totalCycles < 5) return { level: 'very_low', labelKey: 'confidence.veryLow' };
  if (totalCycles < 10) return { level: 'low', labelKey: 'confidence.low' };
  if (totalCycles < 20) return { level: 'medium', labelKey: 'confidence.medium' };
  if (totalCycles < 35) return { level: 'good', labelKey: 'confidence.good' };
  return { level: 'high', labelKey: 'confidence.high' };
}
